import { CONVICTION_SCALE_PPB } from './conviction.js';
import { available, isRate, isU64, unavailable } from './integer.js';
import type { PoolMathResult } from './types.js';

export interface PenaltyTarget {
  targetOutcomeIndex: number | null;
}

/** Contract live_penalty_target; convictions must first be advanced to quote time. */
export function resolvePenaltyTarget(
  outcomeConvictionPpb: readonly bigint[],
  convictionReferenceMarginPpb: bigint,
  outcomeIndices?: readonly number[],
): PoolMathResult<PenaltyTarget> {
  const count = outcomeConvictionPpb.length;
  if (
    count < 2 ||
    count > 10 ||
    (outcomeIndices !== undefined &&
      (outcomeIndices.length !== count ||
        new Set(outcomeIndices).size !== count ||
        outcomeIndices.some(
          (index) => !Number.isSafeInteger(index) || index < 0 || index >= count,
        ))) ||
    !isU64(convictionReferenceMarginPpb) ||
    outcomeConvictionPpb.some((value) => !isRate(value, CONVICTION_SCALE_PPB))
  ) {
    return unavailable('invalid_conviction_state');
  }
  if (convictionReferenceMarginPpb === 0n) return available({ targetOutcomeIndex: null });

  const neutralSharePpb = (CONVICTION_SCALE_PPB + BigInt(count) - 1n) / BigInt(count);
  const referenceBarPpb = neutralSharePpb + convictionReferenceMarginPpb;
  if (!isU64(referenceBarPpb)) return unavailable('invalid_conviction_state');
  let leaderIndex = 0;
  let leaderConvictionPpb = 0n;
  let totalConvictionPpb = 0n;
  for (const [index, value] of outcomeConvictionPpb.entries()) {
    totalConvictionPpb += value;
    const outcomeIndex = outcomeIndices?.[index] ?? index;
    if (
      value > leaderConvictionPpb ||
      (value === leaderConvictionPpb && outcomeIndex < leaderIndex)
    ) {
      leaderIndex = outcomeIndex;
      leaderConvictionPpb = value;
    }
  }
  const reachesReference =
    totalConvictionPpb > 0n &&
    leaderConvictionPpb * CONVICTION_SCALE_PPB >= referenceBarPpb * totalConvictionPpb;
  return available({ targetOutcomeIndex: reachesReference ? leaderIndex : null });
}

export function isPenaltySell(targetOutcomeIndex: number | null, outcomeIndex: number): boolean {
  return targetOutcomeIndex !== null && targetOutcomeIndex !== outcomeIndex;
}
